/**
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * !!  WARNING: Do NOT edit this file!  !!
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 *
 * Foundation components are not intended to be edited to
 * satisfy specific use cases. If you would like to propose
 * a change to this component, please reach out to a
 * frontend engineer.
 *
 * @author Asa Ayers <asa.ayers@hellosign.com>
 * @author Nathan Buchar <nathan.buchar@hellosign.com>
 */

import React from 'react';
import classnames from 'classnames';
import styles from './button.scss';
import { Link, LinkProps } from 'react-router-dom';

const btnMarker = { 'data-btn': 'foundation' };
if (process.env.NODE_ENV === 'development') {
  try {
    // Automatically opt-in for local development and Storybook. You can opt out
    // using the debug toolbar or running:
    // localStorage.highlightButtons = false;
    if (
      localStorage.highlightButtons === undefined
      && (
        window.location.hostname.includes('dev-')
        || window.location.hostname.includes('localhost')
      )
    ) {
      localStorage.highlightButtons = true;
    }

    // localStorage stores strings
    if (localStorage.highlightButtons === 'true') {
      const loader = setInterval(() => {
        // on some pages this may load before `document.body` is avaialble
        if (document.readyState !== 'complete') {
          return;
        }
        clearInterval(loader);
        document.body.classList.add('highlightButtons');
      }, 500);
    }
  } catch (e) {
    // this block intentionally left blank
  }
}

// https://www.figma.com/file/N7i3rROqbDjTqopXdE9KrSMC/Foundation-Components?node-id=1083%3A12
export enum ButtonType {
  Primary,
  Outline,
  Subtle,
  Link,
  Warning,
  Selectable,
  // Icon,
}

export enum ButtonSize {
  Small,
  Standard,
  Large,
}

type BaseButtonProps = {
  // HTML buttons already have a type prop
  kind?: ButtonType
  size?: ButtonSize
  marginless?: boolean

  // Used only for selectable (kind={ButtonType.Selectable})
  // buttons. Do not attempt to use this yourself unless
  // you know what you're doing.
  selected?: Boolean

  className?: React.ButtonHTMLAttributes<HTMLButtonElement>['className']
  disabled?: React.ButtonHTMLAttributes<HTMLButtonElement>['disabled']
  children: React.ButtonHTMLAttributes<HTMLButtonElement>['children']
}

export type ButtonProps = BaseButtonProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  onClick?: (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void | Promise<unknown>
}

export type ButtonLinkProps = BaseButtonProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: React.AnchorHTMLAttributes<HTMLAnchorElement>['href']
  to: never
}

export type ButtonRouterLinkProps = BaseButtonProps & LinkProps


function ButtonImpl({
  kind,
  size,
  disabled = false,
  children,
  selected,
  onClick,
  className,
  ...props
}: ButtonProps, ref: React.Ref<HTMLButtonElement>) {
  const [isProcessing, setProcessing] = React.useState(false);
  const isMounted = React.useRef(true);
  React.useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  const handleClick = React.useCallback(async (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (onClick) {
      const promise = onClick(evt);

      if (promise) {
        setProcessing(true);
        try {
          await promise;
        } finally {
          if (isMounted.current) {
            setProcessing(false);
          }
        }
      }
    }
  }, [onClick]);

  return (
    <button
      ref={ref}
      {...btnMarker}
      onClick={handleClick}
      disabled={disabled || isProcessing}
      className={classnames(styles.button, className, {
        // Disabled is a class so that we can disable the
        // button while processing without getting the
        // disabled style.
        [styles.disabled]: disabled,
        [styles.processing]: isProcessing,
      })}
      {...props}
    >
      {children}
    </button>
  );
}

const ButtonButton = React.forwardRef(ButtonImpl);

function ButtonLinkImpl({
  kind,
  size,
  children,
  to,
  ...props
}: ButtonLinkProps | ButtonRouterLinkProps, ref: React.Ref<HTMLAnchorElement>) {

  if (to) {
    // The docs say the ref is forwarded to the underlying <a but TypeScript doesn't like our ref
    return (
      <Link to={to} ref={ref as any} {...btnMarker} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <a
      ref={ref}
      {...btnMarker}
      {...props}
    >
      {children}
    </a>
  );
}

const ButtonLink = React.forwardRef(ButtonLinkImpl);

function BaseButton({
  kind = ButtonType.Primary,
  size = ButtonSize.Standard,
  marginless = false,
  selected = false,
  className,
  ...rest
}: ButtonProps | ButtonLinkProps | ButtonRouterLinkProps, ref: React.Ref<any>) {

  const props = {
    ...rest,
    kind,
    size,
    className: classnames(styles.button, className, {
      [styles.marginless]: marginless,

      // Button size
      [styles.small]: size === ButtonSize.Small,
      [styles.large]: size === ButtonSize.Large,

      // Button type
      [styles.primary]: kind === ButtonType.Primary,
      [styles.outline]: kind === ButtonType.Outline,
      [styles.subtle]: kind === ButtonType.Subtle,
      [styles.link]: kind === ButtonType.Link,
      [styles.warning]: kind === ButtonType.Warning,
      [styles.selectable]: kind === ButtonType.Selectable,

      // Button selection for selectable buttons
      [styles.selected]: kind === ButtonType.Selectable && selected,
    }),
  };

  if ('href' in props || 'to' in props) {
    return <ButtonLink {...props} ref={ref} />;
  } else {
    return <ButtonButton {...props} ref={ref} />;
  }
}

// Naming this helps VSCode/TypeScript find it for auto-imports
const Button = React.forwardRef(BaseButton);
export default Button;
